<template>
  <DialogPortal>
    <DialogOverlay v-if="!hideBackdrop" class="offcanvas-backdrop animated" />
    <DialogContent
      :class="contentClass"
      :aria-describedby="null"
      v-bind="{ ...useEmitAsProps(emits) }"
      @interact-outside="interactOutsideHandler"
      @escape-key-down="escapeKeyHandler"
    >
      <DialogTitle as-child>
        <div class="offcanvas-header">
          <slot name="title" />
          <DialogClose tabindex="-1" class="btn-close" aria-label="Close" />
        </div>
      </DialogTitle>

      <div :class="['offcanvas-body', bodyClass]">
        <slot />
      </div>
    </DialogContent>
  </DialogPortal>
</template>

<script setup>
import { computed, toRefs } from 'vue'
import {
  DialogClose,
  DialogContent,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  useEmitAsProps,
} from 'radix-vue'

import { RADIX_TO_BOOTSTRAP_PLACEMENT } from '@shared/constants/bootstrap'

const emits = defineEmits([
  'escapeKeyDown',
  'pointerDownOutside',
  'focusOutside',
  'interactOutside',
  'dismiss',
  'openAutoFocus',
  'closeAutoFocus',
])

/* eslint-disable vue/require-default-prop */
const props = defineProps({
  side: {
    type: String,
    default: 'right',
    description: 'Position of the drawer relative to the viewport.',
    validator: (val) => Object.keys(RADIX_TO_BOOTSTRAP_PLACEMENT).includes(val),
  },
  class: {
    type: String,
    required: false,
  },
  bodyClass: {
    type: String,
    required: false,
  },
  staticBackdrop: {
    type: Boolean,
    default: false,
  },
  keyboard: {
    type: Boolean,
    default: false,
  },
  hideBackdrop: {
    type: Boolean,
    default: false,
  },
})

const { side, staticBackdrop, keyboard, class: className } = toRefs(props)

const contentClass = computed(() => (['offcanvas show animated', `offcanvas-${RADIX_TO_BOOTSTRAP_PLACEMENT[side.value]}`, className.value]))

const interactOutsideHandler = (event) => {
  if (staticBackdrop.value) event.preventDefault()
}

const escapeKeyHandler = (event) => {
  if (keyboard.value) event.preventDefault()
}
</script>

<style lang="scss" scoped>
.offcanvas {
  &[data-state='open'] {
    animation-duration: 0.5s;
  }
  &[data-state='closed'] {
    animation-duration: 0.3s;
  }

  &-top {
    &[data-state='open'] {
      animation-name: slide-in-from-top;
    }
    &[data-state='closed'] {
      animation-name: slide-out-from-top;
    }
  }

  &-bottom {
    &[data-state='open'] {
      animation-name: slide-in-from-bottom;
    }
    &[data-state='closed'] {
      animation-name: slide-out-from-bottom;
    }
  }

  &-start {
    &[data-state='open'] {
      animation-name: slide-in-from-left;
    }
    &[data-state='closed'] {
      animation-name: slide-out-from-left;
    }
  }

  &-end {
    &[data-state='open'] {
      animation-name: slide-in-from-right;
    }
    &[data-state='closed'] {
      animation-name: slide-out-from-right;
    }
  }
}

.offcanvas-backdrop {
  background-color: rgba(0, 0, 0, 0.5);
  animation-duration: 0.15s;

  &[data-state='open'] {
    animation-name: fade-in;
  }

  &[data-state='closed'] {
    animation-name: fade-out;
  }
}
</style>
